import { useRef, useState } from "react";
import { Upload, X, Link as LinkIcon, Loader } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ImageUploader({ images = [], onChange, multiple = true }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const inputRef = useRef();

  const addImages = (urls) => {
    const next = multiple ? [...images, ...urls] : [urls[0]];
    onChange(next);
  };

  const remove = (i) => onChange(images.filter((_, j) => j !== i));

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name}: no es una imagen`);
        continue;
      }
      try {
        const form = new FormData();
        form.append("file", file);
        const res = await api.post("/upload/image", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(res.data.url);
      } catch (err) {
        toast.error(`Error subiendo ${file.name}`);
      }
    }
    if (uploaded.length) addImages(uploaded);
    setUploading(false);
  };

  const uploadFromUrl = async () => {
    const url = urlInput.trim();
    if (!url) return;
    setUploading(true);
    try {
      const res = await api.post("/upload/url", { url });
      addImages([res.data.url]);
      setUrlInput("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo descargar la imagen");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    uploadFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3">
      <span className="tracking-eyebrow text-[var(--eafg-muted)] text-[0.72rem]">Imágenes</span>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`border-2 border-dashed cursor-pointer transition-colors flex flex-col items-center justify-center gap-2 py-8 px-4 text-center select-none
          ${dragging
            ? "border-[var(--eafg-copper)] bg-[#fff5f0]"
            : "border-[var(--eafg-line)] hover:border-[var(--eafg-copper)] hover:bg-[#fafaf9]"
          } ${uploading ? "pointer-events-none opacity-60" : ""}`}
      >
        {uploading
          ? <Loader size={22} strokeWidth={1.5} className="animate-spin text-[var(--eafg-copper)]" />
          : <Upload size={22} strokeWidth={1.5} className="text-[var(--eafg-muted)]" />
        }
        <p className="tracking-eyebrow text-[0.72rem] text-[var(--eafg-muted)]">
          {uploading ? "Subiendo…" : "Arrastra imágenes aquí o haz clic para seleccionar"}
        </p>
        <p className="text-xs text-[var(--eafg-muted)]">JPG, PNG, WebP · máx. 8 MB</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
        />
      </div>

      {/* URL input — downloads the image to the server */}
      <div className="flex gap-2 items-center">
        <LinkIcon size={13} strokeWidth={1.5} className="text-[var(--eafg-muted)] shrink-0" />
        <input
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), uploadFromUrl())}
          placeholder="O pega una URL de imagen…"
          disabled={uploading}
          className="flex-1 border-b border-[var(--eafg-line)] bg-transparent py-1.5 text-sm focus:outline-none focus:border-[var(--eafg-ink)] disabled:opacity-50"
        />
        <button
          type="button"
          onClick={uploadFromUrl}
          disabled={uploading || !urlInput.trim()}
          className="border border-[var(--eafg-ink)] px-3 py-1.5 tracking-eyebrow text-xs hover:bg-[var(--eafg-ink)] hover:text-white transition-colors disabled:opacity-40"
        >
          {uploading ? "…" : "+"}
        </button>
      </div>

      {/* Preview grid */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {images.map((src, i) => (
            <div key={i} className="relative group">
              <img src={src} alt="" className="w-20 h-20 object-cover border border-[var(--eafg-line)]" />
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute -top-2 -right-2 bg-[var(--eafg-ink)] text-white w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={10} />
              </button>
              {i === 0 && (
                <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] tracking-wide text-center py-0.5">
                  PRINCIPAL
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
